import { Injectable } from '@angular/core';
import { EfateCakeDataSetModel, EfateCakeDataSetParameter } from 'src/app/shared/models/echo/efate-cake-data-set';
import { CakeTopologyBidirectionalDirection } from 'src/app/shared/models/ef-cake-topology.model';
import {
  EfCompartmentTopology,
  EfMetaboliteParameters,
  EfModelParameterDefinition,
  EfModelParameterRow,
  EfModelParametersSet,
  EfParametersTopology,
} from 'src/app/shared/models/ef-model-parameters.model';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfCakeTopologyStateUtils } from '../../shared/utils/ef-cake-topology-state.utils';

@Injectable({ providedIn: 'root' })
export class EfDataModelParametersLogicService {
  /** Derives, from the selected group, which compartments exist, their targets, sinks and which metabolites are primary. */
  buildTopology(
    activeEdges: Set<string>,
    activeStatusNodes: Set<string>,
    bidirectionalEdges: Record<string, CakeTopologyBidirectionalDirection>,
    isParentOnly: boolean,
  ): EfParametersTopology {
    const adjacency = this.buildAdjacency(activeEdges, bidirectionalEdges);
    const parentKey = EFateConstants.PARENT_COMPARTMENT_KEY;
    const hasSink = (nodeKey: string): boolean => [...activeStatusNodes]
      .some((statusNode: string) => EFateConstants.STATUS_NODE_TO_OWNER_NODE[statusNode] === nodeKey);
    const getTargets = (nodeKey: string): string[] => isParentOnly ? [] : [...(adjacency.get(nodeKey) ?? [])].sort();
    const isFedByMetabolite = (nodeKey: string): boolean => [...adjacency.entries()]
      .some(([sourceKey, targets]: [string, Set<string>]) => sourceKey !== parentKey && targets.has(nodeKey));

    const metaboliteKeys = isParentOnly ? [] : EFateConstants.TOPOLOGY_NODE_KEYS
      .filter((nodeKey: string) => nodeKey !== parentKey && EfCakeTopologyStateUtils.isNodeActivated(nodeKey, activeEdges, bidirectionalEdges))
      .sort();

    return {
      parent: { nodeKey: parentKey, hasSink: hasSink(parentKey), targetKeys: getTargets(parentKey), isPrimary: false },
      metabolites: metaboliteKeys.map((nodeKey: string) => ({
        nodeKey,
        hasSink: hasSink(nodeKey),
        targetKeys: getTargets(nodeKey),
        isPrimary: EFateConstants.PRIMARY_METABOLITE_KEYS.includes(nodeKey) && !isFedByMetabolite(nodeKey),
      })),
    };
  }

  /** Builds the dialog state: defaults overlaid with the user's saved edits that still apply to the topology. */
  createParametersSet(
    parentModelType: string,
    topology: EfParametersTopology,
    nodeNames: Record<string, string>,
    saved?: EfModelParametersSet,
  ): EfModelParametersSet {
    return {
      parentRows: this.restoreRows(this.createParentRows(parentModelType, topology.parent, nodeNames), saved?.parentRows),
      metabolites: topology.metabolites.map((compartment: EfCompartmentTopology) =>
        this.restoreMetabolite(compartment, nodeNames, saved?.metabolites.find((metabolite: EfMetaboliteParameters) => metabolite.nodeKey === compartment.nodeKey))),
    };
  }

  createMetabolite(compartment: EfCompartmentTopology, modelType: string, nodeNames: Record<string, string>): EfMetaboliteParameters {
    const name = nodeNames[compartment.nodeKey] || compartment.nodeKey;

    return {
      nodeKey: compartment.nodeKey,
      modelType,
      rows: this.createRows(EFateConstants.METABOLITE_INITIAL_AMOUNT_PARAMETER, modelType, compartment, nodeNames, `${name} `),
    };
  }

  /** Model rows to persist for one parent model: the parent itself plus its metabolites, each with its parameters. */
  toModels(parentModelType: string, isSelected: boolean, set?: EfModelParametersSet): EfateCakeDataSetModel[] {
    const parentModel: EfateCakeDataSetModel = {
      parentModelType,
      compartmentKey: EFateConstants.PARENT_COMPARTMENT_KEY,
      modelType: parentModelType,
      isSelected,
      parameters: set ? this.toParameters(set.parentRows) : undefined,
    };
    const metaboliteModels = (set?.metabolites ?? []).map((metabolite: EfMetaboliteParameters): EfateCakeDataSetModel => ({
      parentModelType,
      compartmentKey: metabolite.nodeKey,
      modelType: metabolite.modelType,
      isSelected,
      parameters: this.toParameters(metabolite.rows),
    }));

    return [parentModel, ...metaboliteModels];
  }

  /** Rebuilds the in-memory parameter sets, by parent model, from the persisted model rows. */
  fromModels(models?: EfateCakeDataSetModel[]): Record<string, EfModelParametersSet> {
    const sets: Record<string, EfModelParametersSet> = {};

    (models ?? [])
      .filter((model: EfateCakeDataSetModel) => model.compartmentKey === EFateConstants.PARENT_COMPARTMENT_KEY && (model.parameters?.length ?? 0) > 0)
      .forEach((parentModel: EfateCakeDataSetModel) => {
        sets[parentModel.parentModelType] = {
          parentRows: this.toRows(parentModel.parameters),
          metabolites: models!
            .filter((model: EfateCakeDataSetModel) => model.parentModelType === parentModel.parentModelType
              && model.compartmentKey !== EFateConstants.PARENT_COMPARTMENT_KEY && (model.parameters?.length ?? 0) > 0)
            .map((model: EfateCakeDataSetModel): EfMetaboliteParameters => ({
              nodeKey: model.compartmentKey,
              modelType: model.modelType,
              rows: this.toRows(model.parameters),
            })),
        };
      });

    return sets;
  }

  cloneSet(set: EfModelParametersSet): EfModelParametersSet {
    return {
      parentRows: this.cloneRows(set.parentRows),
      metabolites: set.metabolites.map((metabolite: EfMetaboliteParameters) => ({ ...metabolite, rows: this.cloneRows(metabolite.rows) })),
    };
  }

  /** Cleared initial values fall back to the model defaults so the closed dialog never keeps an empty one. */
  fillMissingInitialValues(
    set: EfModelParametersSet,
    parentModelType: string,
    topology: EfParametersTopology,
    nodeNames: Record<string, string>,
  ): void {
    this.fillRows(set.parentRows, this.createParentRows(parentModelType, topology.parent, nodeNames));

    set.metabolites.forEach((metabolite: EfMetaboliteParameters) => {
      const compartment = topology.metabolites.find((candidate: EfCompartmentTopology) => candidate.nodeKey === metabolite.nodeKey);
      if (compartment) {
        this.fillRows(metabolite.rows, this.createMetabolite(compartment, metabolite.modelType, nodeNames).rows);
      }
    });
  }

  setInitialValue(row: EfModelParameterRow, value: number | null): void {
    row.initialValue = value;
    this.clampInitialValue(row);
  }

  setLowerLimit(row: EfModelParameterRow, value: number | null): void {
    row.lowerLimit = value;

    if (value !== null && row.upperLimit !== null && value >= row.upperLimit) {
      row.upperLimit = this.round(value + this.getLimitGap(value));
    }

    this.clampInitialValue(row);
  }

  setUpperLimit(row: EfModelParameterRow, value: number | null): void {
    row.upperLimit = value;

    if (value !== null && row.lowerLimit !== null && value <= row.lowerLimit) {
      row.lowerLimit = this.round(value - this.getLimitGap(value));
    }

    this.clampInitialValue(row);
  }

  private toParameters(rows: EfModelParameterRow[]): EfateCakeDataSetParameter[] {
    return rows.map((row: EfModelParameterRow) => ({
      parameterName: row.key,
      initialValue: row.initialValue,
      lowerLimit: row.lowerLimit,
      upperLimit: row.upperLimit,
      isFixed: row.isFixed,
    }));
  }

  // Labels and the automatic flag are not stored; restoreRows takes them from the current defaults.
  private toRows(parameters?: EfateCakeDataSetParameter[]): EfModelParameterRow[] {
    return (parameters ?? []).map((parameter: EfateCakeDataSetParameter) => ({
      key: parameter.parameterName,
      label: parameter.parameterName,
      initialValue: parameter.initialValue ?? null,
      isFixed: parameter.isFixed,
      lowerLimit: parameter.lowerLimit ?? null,
      upperLimit: parameter.upperLimit ?? null,
      isAutomatic: false,
    }));
  }

  private fillRows(rows: EfModelParameterRow[], defaultRows: EfModelParameterRow[]): void {
    rows
      .filter((row: EfModelParameterRow) => !row.isAutomatic && row.initialValue === null)
      .forEach((row: EfModelParameterRow) => {
        const defaultValue = defaultRows.find((defaultRow: EfModelParameterRow) => defaultRow.key === row.key)?.initialValue ?? null;
        this.setInitialValue(row, defaultValue);
      });
  }

  private createParentRows(modelType: string, compartment: EfCompartmentTopology, nodeNames: Record<string, string>): EfModelParameterRow[] {
    return this.createRows(EFateConstants.PARENT_INITIAL_AMOUNT_PARAMETER, modelType, compartment, nodeNames, '');
  }

  private createRows(
    amountDefinition: EfModelParameterDefinition,
    modelType: string,
    compartment: EfCompartmentTopology,
    nodeNames: Record<string, string>,
    labelPrefix: string,
  ): EfModelParameterRow[] {
    return [
      amountDefinition,
      ...(EFateConstants.MODEL_PARAMETER_DEFINITIONS[modelType] ?? []),
      ...this.buildFlowDefinitions(compartment, nodeNames),
    ].map((definition: EfModelParameterDefinition) => this.toRow({ ...definition, label: `${labelPrefix}${definition.label}` }));
  }

  private restoreMetabolite(
    compartment: EfCompartmentTopology,
    nodeNames: Record<string, string>,
    saved?: EfMetaboliteParameters,
  ): EfMetaboliteParameters {
    const modelType = saved?.modelType === EFateConstants.DFOP_MODEL && compartment.isPrimary
      ? EFateConstants.DFOP_MODEL
      : EFateConstants.DEFAULT_METABOLITE_MODEL;
    const defaults = this.createMetabolite(compartment, modelType, nodeNames);

    return saved?.modelType === modelType ? { ...defaults, rows: this.restoreRows(defaults.rows, saved.rows) } : defaults;
  }

  // Keeps the user's edits only for parameters that still exist in the current topology.
  private restoreRows(defaultRows: EfModelParameterRow[], savedRows?: EfModelParameterRow[]): EfModelParameterRow[] {
    return defaultRows.map((defaultRow: EfModelParameterRow) => {
      const savedRow = savedRows?.find((row: EfModelParameterRow) => row.key === defaultRow.key);
      return savedRow ? { ...savedRow, label: defaultRow.label, isAutomatic: defaultRow.isAutomatic } : defaultRow;
    });
  }

  private cloneRows(rows: EfModelParameterRow[]): EfModelParameterRow[] {
    return rows.map((row: EfModelParameterRow) => ({ ...row }));
  }

  private buildAdjacency(
    activeEdges: Set<string>,
    bidirectionalEdges: Record<string, CakeTopologyBidirectionalDirection>,
  ): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();
    const addEdge = (source?: string, target?: string): void => {
      if (!source || !target) {
        return;
      }

      adjacency.set(source, (adjacency.get(source) ?? new Set<string>()).add(target));
    };

    activeEdges.forEach((edgeId: string) => addEdge(EFateConstants.EDGE_TO_SOURCE_NODE[edgeId], EFateConstants.EDGE_TO_TARGET_NODE[edgeId]));

    Object.entries(EFateConstants.BIDIRECTIONAL_EDGE_NODES).forEach(([edgeId, nodes]: [string, { from: string; to: string }]) => {
      const direction = bidirectionalEdges[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;

      if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD) {
        addEdge(nodes.from, nodes.to);
      } else if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE) {
        addEdge(nodes.to, nodes.from);
      }
    });

    return adjacency;
  }

  private buildFlowDefinitions(compartment: EfCompartmentTopology, nodeNames: Record<string, string>): EfModelParameterDefinition[] {
    const { FLOW_KEY_PREFIX, FLOW_LABEL_PREFIX, FLOW_LOWER_LIMIT, FLOW_UPPER_LIMIT, FLOW_DECIMALS } = EFateConstants.MODEL_PARAMETERS;
    const targets = [...compartment.targetKeys].sort();

    if (targets.length === 0) {
      return [];
    }

    // Without a sink the last fraction is implicit (1 - sum of the others), so it is not editable.
    const visibleTargets = compartment.hasSink ? targets : targets.slice(0, -1);
    const initialValue = this.round(1 / (compartment.hasSink ? targets.length + 1 : targets.length), FLOW_DECIMALS);

    return visibleTargets.map((targetKey: string) => ({
      key: `${FLOW_KEY_PREFIX}${targetKey}`,
      label: `${FLOW_LABEL_PREFIX} ${nodeNames[targetKey] || targetKey}`,
      initialValue,
      lowerLimit: FLOW_LOWER_LIMIT,
      upperLimit: FLOW_UPPER_LIMIT,
    }));
  }

  private toRow(definition: EfModelParameterDefinition): EfModelParameterRow {
    return {
      key: definition.key,
      label: definition.label,
      initialValue: definition.initialValue,
      isFixed: definition.isFixed ?? false,
      lowerLimit: definition.lowerLimit,
      upperLimit: definition.upperLimit,
      isAutomatic: definition.isAutomatic ?? false,
    };
  }

  private clampInitialValue(row: EfModelParameterRow): void {
    if (row.initialValue === null) {
      return;
    }

    if (row.lowerLimit !== null && row.initialValue < row.lowerLimit) {
      row.initialValue = row.lowerLimit;
    }

    if (row.upperLimit !== null && row.initialValue > row.upperLimit) {
      row.initialValue = row.upperLimit;
    }
  }

  private getLimitGap(limit: number): number {
    const { LIMIT_GAP_RATIO, MIN_LIMIT_GAP } = EFateConstants.MODEL_PARAMETERS;
    return Math.max(Math.abs(limit) * LIMIT_GAP_RATIO, MIN_LIMIT_GAP);
  }

  private round(value: number, decimals: number = 10): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }
}
